import { useState } from 'react';

import { Button } from '@/components/ui/button';

import { useDraft } from '../builder/useDraftContext';
import {
  deletePhase,
  renamePhase,
  renamedPhaseId,
  setPhaseId,
} from '../builder/draftOps';
import { idErrorFor, slugify } from '../builder/identifiers';
import type { BuilderSelection } from '../builder/selection';
import { UNASSIGNED_PHASE_ID } from '../builder/types';
import { useWorkspace } from '@/features/workspaces';
import { phaseDeletion } from '../phaseDeletion';
import { useFieldError } from '../builder/useFieldError';
import { CommitField } from './CommitField';
import { DeletePhaseDialog } from './DeletePhaseDialog';

/**
 * A phase: its name, its identifier, and getting rid of it.
 *
 * A phase is a board column, which is why its order and its identifier matter
 * beyond this canvas — and why deleting one now takes its steps with it, and
 * is refused outright while a task is standing on one of them.
 */
export function PhaseInspector({
  workspaceId,
  phaseId,
  onSelect,
}: {
  workspaceId: string;
  phaseId: string;
  onSelect: (selection: BuilderSelection) => void;
}) {
  const { draft, edit } = useDraft();
  // Declared before the early return below, because a hook must be — deleting
  // the selected phase takes this body away, and a hook called past that return
  // would change the hook order between two renders of the same component.
  const { resolve: resolveName } = useFieldError('name');
  const [confirming, setConfirming] = useState(false);
  const phase = draft.phases.find((candidate) => candidate.id === phaseId);
  // The aggregate the page has already loaded — the same source
  // `DeleteWorkflowDialog` counts against, rather than a request of its own.
  const workspace = useWorkspace(workspaceId);

  if (!phase) return null;

  // The holding pen is the builder's, not the user's: renaming or deleting it
  // would be editing a piece of the tool rather than a piece of the workflow.
  if (phaseId === UNASSIGNED_PHASE_ID) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-foreground text-sm">Unassigned</p>
        <p className="text-muted-foreground text-xs">
          Steps that are not in a phase yet. They can be saved here, but a
          workflow cannot be published while any remain — a step outside every
          phase has no board column, so a task on it would be invisible.
        </p>
        <p className="text-muted-foreground text-xs">
          Drag a step into a phase band to place it.
        </p>
      </div>
    );
  }

  // Computed after the pen's early return, not before it: that branch renders
  // nothing that needs either the outcome or the workspace query.
  //
  // Advisory, deliberately. The aggregate can be stale — someone else can add a
  // task between this check and the save — and the service refuses the save with
  // `WORKFLOW_STEPS_IN_USE` regardless. This exists to move that refusal to the
  // moment of the decision instead of letting it ambush a save made long after.
  const tasksPending = workspace.isPending;
  const outcome = phaseDeletion(
    phase,
    draft.loaded.id,
    workspace.data?.tasks ?? [],
  );

  // Matches the step inspector, and for the same reason: a phase id is a board
  // column's identity, and tasks are positioned by step within it (FR-036).
  const identifierLocked = draft.status === 'published';
  const idError = idErrorFor(draft, phase.id);

  return (
    <div className="flex flex-col gap-5">
      <CommitField
        label="Name"
        value={phase.name}
        required
        field="name"
        onCommit={(name) => {
          // Same as a step: the id follows, so the selection must follow it.
          const nextId = renamedPhaseId(draft, phaseId, name);
          edit(renamePhase(phaseId, name));
          resolveName();
          if (nextId !== phaseId) onSelect({ kind: 'phase', id: nextId });
        }}
        hint="Shown as this phase’s board column."
      />

      <div className="flex flex-col gap-1.5">
        <CommitField
          label="Identifier"
          value={phase.id}
          mono
          readOnly={identifierLocked}
          onCommit={(next) => {
            const candidate = slugify(next);
            if (candidate === phase.id) return;
            if (idErrorFor(draft, candidate, phase.id)) return;
            edit(setPhaseId(phase.id, candidate));
            onSelect({ kind: 'phase', id: candidate });
          }}
          hint={
            identifierLocked
              ? 'Fixed once published: this names a board column that tasks already sit in.'
              : 'Follows the name until you set it yourself.'
          }
        />
        {idError && (
          <p role="alert" className="text-destructive text-xs">
            {idError}
          </p>
        )}
      </div>

      <p className="text-muted-foreground text-xs">
        {phase.steps.length === 1
          ? '1 step in this phase.'
          : `${String(phase.steps.length)} steps in this phase.`}
      </p>

      <Button
        variant="outline"
        size="sm"
        // Never while the tasks are still arriving. `workspace.data?.tasks ?? []`
        // is indistinguishable from "this workflow has no tasks", so deleting
        // during the load would take the confirm path and destroy a step a task
        // is standing on. Waiting is the only honest answer to a question whose
        // data has not arrived.
        disabled={tasksPending}
        title={
          tasksPending
            ? 'Checking whether any tasks use these steps…'
            : undefined
        }
        // Three paths, decided here rather than inside the dialog, so which one
        // applies is visible at the point the choice is made. An empty phase
        // loses nothing and goes straight away; one whose steps carry tasks
        // cannot go at all; anything else destroys work and is asked about
        // first.
        onClick={() => {
          if (outcome.kind === 'empty') {
            edit(deletePhase(phaseId));
            onSelect(null);
            return;
          }
          setConfirming(true);
        }}
      >
        Delete this phase
      </Button>

      <DeletePhaseDialog
        phaseName={phase.name}
        stepNames={outcome.kind === 'confirm' ? outcome.stepNames : []}
        blocked={outcome.kind === 'blocked' ? outcome.blocked : undefined}
        open={confirming}
        onOpenChange={setConfirming}
        onConfirm={() => {
          edit(deletePhase(phaseId));
          onSelect(null);
        }}
      />
    </div>
  );
}
